import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, switchMap, finalize, tap, shareReplay } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../services/token.service';
import * as AuthActions from '../store/auth/auth.actions';
import * as GameSessionActions from '../store/game-session/game-session.actions';
import { SKIP_AUTH } from '../tokens';


// interface TokenResponse {
//   accessToken: string;
//   refreshToken: string;
// }

// Module-level gate for a single in-flight refresh shared by all requests.
// let refresh$: Observable<TokenResponse> | null = null;

// const isPublicAuthUrl = (url: string) => /\/auth\/(login|register|forgot-password|reset-password|verify-email|verify-reset-token|revoke)(\/|$)/.test(url);

const isRefreshUrl = (url: string) => /\/auth\/refresh(\/|$)/.test(url);

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authService  = inject(AuthService);
  const store        = inject(Store);

  // Skip auth (login/register/refresh) via HttpContext:
  if (req.context.get(SKIP_AUTH)) return next(req);

  const access = tokenService.getAccessToken();
  const authReq = access ? req.clone({ setHeaders: { Authorization: `Bearer ${access}` } }) : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 && !isRefreshUrl(req.url)) return throwError(() => err);
      if (!tokenService.hasRefreshToken()) {
        tokenService.removeTokens();
        store.dispatch(AuthActions.logout());
        store.dispatch(GameSessionActions.clearCurrentSession());
        return throwError(() => err);
      }
      // Ask the service to refresh (coalesced). Then retry once.
      return authService.refreshToken().pipe(
        switchMap(() => {
          const newAccess = tokenService.getAccessToken();
          const retried = newAccess
            ? req.clone({ setHeaders: { Authorization: `Bearer ${newAccess}` } })
            : req;
          return next(retried);
        })
      );
    })
  );
};
